import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Trash2,
  RotateCcw,
  RefreshCw,
  X,
  AlertTriangle,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Users,
  Phone,
  Mail,
  Calendar,
  Tag,
  User,
  MapPin,
  CreditCard,
  Clock,
  Globe,
  FileText,
  Filter,
  Database,
  FileDown,
  Trash,
  ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';

const CustomersPage = () => {
  const { api, isAdmin, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState('browse');
  
  // Browse State
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('phone');
  const [recordTypeFilter, setRecordTypeFilter] = useState('all'); // 'all', 'sale_done', 'error'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [hardDelete, setHardDelete] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Upload State
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadRecordType, setUploadRecordType] = useState('sale_done'); // 'sale_done' or 'error'

  // Export State
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    date_from: '',
    date_to: '',
    offer_name: '',
    agent: ''
  });

  const fetchCustomers = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (searchQuery) {
        if (searchType === 'phone') params.append('phone', searchQuery);
        else if (searchType === 'email') params.append('email', searchQuery);
        else if (searchType === 'agent') params.append('search', searchQuery);
      }

      // Add record type filter (show all by default)
      if (recordTypeFilter && recordTypeFilter !== 'all') {
        params.append('record_type', recordTypeFilter);
      }

      const response = await api.get(`/customers?${params.toString()}`);
      setCustomers(response.data.data);
      setPagination({
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        pages: response.data.pages,
      });
    } catch (error) {
      console.error('Fetch customers error:', error);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [recordTypeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(1);
  };

  const handleRecordTypeFilterChange = (value) => {
    setRecordTypeFilter(value);
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (customer, isHardDelete = false) => {
    setCustomerToDelete(customer);
    setHardDelete(isHardDelete);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      if (hardDelete) {
        await api.delete(`/customers/${customerToDelete.id}/hard`);
        toast.success('Customer permanently deleted');
      } else {
        await api.delete(`/customers/${customerToDelete.id}`);
        toast.success('Customer moved to trash');
      }
      fetchCustomers(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      await api.delete('/customers/delete-all');
      toast.success('All records deleted successfully');
      fetchCustomers(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete all records');
    } finally {
      setDeletingAll(false);
      setDeleteAllDialogOpen(false);
    }
  };

  const handleRestore = async (customerId) => {
    try {
      await api.post(`/customers/${customerId}/restore`);
      toast.success('Customer restored');
      fetchCustomers(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore customer');
    }
  };

  // Upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        setUploadResult(null);
      }
    }
  }, []);

  const validateFile = (file) => {
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!hasValidExtension) {
      toast.error('Please upload a CSV or Excel file');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        setUploadResult(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/customers/upload?record_type=${uploadRecordType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadResult(response.data);
      toast.success(`Successfully uploaded ${response.data.records_uploaded} ${uploadRecordType === 'sale_done' ? 'sale' : 'error'} records`);
      fetchCustomers(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadResult({ error: true, message: error.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = (type = 'sale_done') => {
    let headers, filename;
    if (type === 'sale_done') {
      headers = ['DATE', 'WEBSITE', 'OFFER NAME', 'CREDIT SCORE', 'VERIFICATION ID', 'FIRST NAME', 'LAST NAME', 'NAME', 'REASON', 'EST TIME', 'ADDRESS', 'CITY', 'STATE', 'ZIPCODE', 'EMAIL', 'PHONE NUMBER'];
      filename = 'sales_upload_template.csv';
    } else {
      headers = ['DATE', 'WEBSITE', 'OFFER NAME', 'NAME', 'ERROR', 'PHONE NO', 'AGENT NAME', 'EMAIL', 'BANK'];
      filename = 'error_upload_template.csv';
    }
    const csvContent = headers.join(',') + '\n' + headers.map(() => '').join(',');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const clearFile = () => {
    setFile(null);
    setUploadResult(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Export handler
  const handleExport = async (withFilters = true) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: exportFormat });
      
      if (withFilters) {
        if (exportFilters.date_from) params.append('date_from', exportFilters.date_from);
        if (exportFilters.date_to) params.append('date_to', exportFilters.date_to);
        if (exportFilters.offer_name) params.append('offer_name', exportFilters.offer_name);
        if (exportFilters.agent) params.append('agent', exportFilters.agent);
      }

      const response = await api.get(`/customers/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: exportFormat === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export completed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Sales Management</h1>
          <p className="text-muted-foreground mt-1">
            Browse, upload, and export sales data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
            <Database className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{pagination.total.toLocaleString()} Records</span>
          </div>
          {isAdmin() && pagination.total > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteAllDialogOpen(true)}
              className="gap-2"
            >
              <Trash className="w-4 h-4" />
              Delete All
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl">
          <TabsTrigger value="browse" className="rounded-lg gap-2 data-[state=active]:shadow-sm">
            <Users className="w-4 h-4" />
            Browse
          </TabsTrigger>
          {(isManager() || isAdmin()) && (
            <TabsTrigger value="upload" className="rounded-lg gap-2 data-[state=active]:shadow-sm">
              <Upload className="w-4 h-4" />
              Upload
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="export" className="rounded-lg gap-2 data-[state=active]:shadow-sm">
              <Download className="w-4 h-4" />
              Export
            </TabsTrigger>
          )}
        </TabsList>

        {/* Browse Tab */}
        <TabsContent value="browse" className="space-y-4 mt-0">
          {/* Search Bar */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 flex gap-2">
                  <Select value={searchType} onValueChange={setSearchType}>
                    <SelectTrigger className="w-[140px] bg-background" data-testid="search-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" /> Phone
                        </div>
                      </SelectItem>
                      <SelectItem value="email">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Email
                        </div>
                      </SelectItem>
                      <SelectItem value="agent">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" /> Agent
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      placeholder={`Search by ${searchType}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 bg-background"
                      data-testid="customer-search-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* Record Type Filter */}
                  <Select value={recordTypeFilter} onValueChange={handleRecordTypeFilterChange}>
                    <SelectTrigger className="w-[140px] bg-background" data-testid="record-type-filter">
                      <SelectValue placeholder="All Records" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Database className="w-4 h-4" /> All Records
                        </div>
                      </SelectItem>
                      <SelectItem value="sale_done">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Sales
                        </div>
                      </SelectItem>
                      <SelectItem value="error">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" /> Errors
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="submit" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" data-testid="search-btn">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setSearchQuery(''); setRecordTypeFilter('all'); fetchCustomers(1); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-0 shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
                  <tr>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[80px]">Type</th>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[100px]">Date</th>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[150px]">Name</th>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[140px]">Phone</th>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[150px]">Email</th>
                    <th className="py-4 px-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[100px]">Offer</th>
                    <th className="py-4 px-4 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider w-[150px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
                      </td>
                    </tr>
                  ) : customers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No records found</p>
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer) => (
                      <tr key={customer.id} className={`hover:bg-muted/50 transition-colors ${customer.is_deleted ? 'opacity-50' : ''}`}>
                        <td className="py-4 px-4">
                          {customer.record_type === 'error' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Error
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Sale
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{customer.date || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-medium text-foreground">
                            {customer.first_name || ''} {customer.last_name || ''}
                            {!customer.first_name && !customer.last_name && (customer.name || '-')}
                          </div>
                          {customer.record_type === 'error' && customer.agent_name && (
                            <div className="text-xs text-muted-foreground">Agent: {customer.agent_name}</div>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{customer.phone_number || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground truncate max-w-[140px]">{customer.email || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {customer.offer_name ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              <Tag className="w-3 h-3 mr-1" />
                              {customer.offer_name}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-2 relative z-10">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleViewCustomer(customer); }}
                              className="gap-1 whitespace-nowrap"
                              data-testid={`view-customer-${customer.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            {(isManager() || isAdmin()) && !customer.is_deleted && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="z-50">
                                  <DropdownMenuItem onClick={() => handleDeleteClick(customer)} className="text-amber-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Move to Trash
                                  </DropdownMenuItem>
                                  {isAdmin() && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleDeleteClick(customer, true)} className="text-destructive">
                                        <AlertTriangle className="w-4 h-4 mr-2" />
                                        Delete Permanently
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {isAdmin() && customer.is_deleted && (
                              <Button variant="ghost" size="sm" onClick={() => handleRestore(customer.id)}>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restore
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fetchCustomers(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium px-3">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fetchCustomers(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Upload Tab */}
        {(isManager() || isAdmin()) && (
          <TabsContent value="upload" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <Upload className="w-6 h-6" />
                      Upload Records
                    </h3>
                    <p className="text-emerald-100 mt-1">Import your sales or error data from CSV or Excel files</p>
                  </div>
                  <CardContent className="p-6">
                    {/* Record Type Selection */}
                    <div className="mb-6">
                      <Label className="text-sm font-medium mb-3 block">Record Type</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            uploadRecordType === 'sale_done'
                              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                              : 'border-border hover:border-emerald-300'
                          }`}
                          onClick={() => setUploadRecordType('sale_done')}
                          data-testid="record-type-sale"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              uploadRecordType === 'sale_done' 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-slate-100 dark:bg-slate-800'
                            }`}>
                              <CheckCircle2 className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">Sale Done</p>
                              <p className="text-xs text-muted-foreground">Successful sales records</p>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            uploadRecordType === 'error'
                              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                              : 'border-border hover:border-red-300'
                          }`}
                          onClick={() => setUploadRecordType('error')}
                          data-testid="record-type-error"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              uploadRecordType === 'error' 
                                ? 'bg-red-500 text-white' 
                                : 'bg-slate-100 dark:bg-slate-800'
                            }`}>
                              <AlertCircle className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">Error</p>
                              <p className="text-xs text-muted-foreground">Failed/error records</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Download Template Button */}
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileDown className="w-8 h-8 text-blue-600" />
                          <div>
                            <p className="font-medium text-blue-900 dark:text-blue-100">Download Template</p>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              Get the {uploadRecordType === 'sale_done' ? 'Sales' : 'Error'} format template
                            </p>
                          </div>
                        </div>
                        <Button onClick={() => downloadTemplate(uploadRecordType)} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                          <Download className="w-4 h-4 mr-2" />
                          Download CSV
                        </Button>
                      </div>
                    </div>

                    {/* Dropzone */}
                    <div
                      className={`relative border-2 border-dashed rounded-2xl p-8 transition-all duration-300 ${
                        dragActive
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : file
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10'
                          : 'border-slate-300 dark:border-slate-700 hover:border-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileSelect}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        data-testid="file-input"
                      />

                      {file ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                              <FileSpreadsheet className="w-7 h-7 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{file.name}</p>
                              <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); clearFile(); }}>
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 flex items-center justify-center mx-auto mb-4">
                            <Upload className="w-10 h-10 text-emerald-600" />
                          </div>
                          <p className="text-xl font-semibold text-foreground mb-1">Drop your file here</p>
                          <p className="text-muted-foreground mb-4">or click to browse</p>
                          <p className="text-xs text-muted-foreground">Supports CSV, XLS, XLSX up to 10MB</p>
                        </div>
                      )}
                    </div>

                    {file && !uploadResult && (
                      <Button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="w-full mt-6 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                        data-testid="upload-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 mr-2" />
                            Upload Records
                          </>
                        )}
                      </Button>
                    )}

                    {uploadResult && (
                      <div className="mt-6 animate-fade-in">
                        {uploadResult.error ? (
                          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                            <div className="flex items-center gap-3">
                              <AlertCircle className="w-5 h-5 text-red-500" />
                              <p className="font-medium text-red-700 dark:text-red-400">Upload Failed</p>
                            </div>
                            <p className="mt-2 text-sm text-red-600">{uploadResult.message}</p>
                          </div>
                        ) : (
                          <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                            <div className="flex items-center gap-3 mb-4">
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                              <p className="font-medium text-emerald-700 dark:text-emerald-400">Upload Completed!</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-center">
                              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <p className="text-2xl font-bold text-emerald-600">{uploadResult.records_uploaded}</p>
                                <p className="text-xs text-muted-foreground">Records Added</p>
                              </div>
                              <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                                <p className="text-2xl font-bold text-red-600">{uploadResult.errors}</p>
                                <p className="text-xs text-muted-foreground">Errors</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <Button variant="outline" onClick={clearFile} className="w-full mt-4">
                          Upload Another File
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Instructions */}
              <div className="space-y-4">
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Expected Columns ({uploadRecordType === 'sale_done' ? 'Sales' : 'Error'})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      {uploadRecordType === 'sale_done' ? (
                        ['DATE', 'WEBSITE', 'OFFER NAME', 'CREDIT SCORE', 'VERIFICATION ID', 'FIRST NAME', 'LAST NAME', 'NAME', 'REASON', 'EST TIME', 'ADDRESS', 'CITY', 'STATE', 'ZIPCODE', 'EMAIL', 'PHONE NUMBER'].map(col => (
                          <div key={col} className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50" />
                            {col}
                          </div>
                        ))
                      ) : (
                        ['DATE', 'WEBSITE', 'OFFER NAME', 'NAME', 'ERROR', 'PHONE NO', 'AGENT NAME', 'EMAIL', 'BANK'].map(col => (
                          <div key={col} className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500/50" />
                            {col}
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className={`border-0 shadow-lg ${uploadRecordType === 'sale_done' 
                  ? 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20'
                  : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {uploadRecordType === 'sale_done' ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div>
                        <p className={`font-medium text-sm ${uploadRecordType === 'sale_done' 
                          ? 'text-emerald-800 dark:text-emerald-300' 
                          : 'text-red-800 dark:text-red-300'
                        }`}>
                          {uploadRecordType === 'sale_done' ? 'Sales Records' : 'Error Records'}
                        </p>
                        <p className={`text-xs mt-1 ${uploadRecordType === 'sale_done' 
                          ? 'text-emerald-700 dark:text-emerald-400' 
                          : 'text-red-700 dark:text-red-400'
                        }`}>
                          {uploadRecordType === 'sale_done' 
                            ? 'These records count towards sales statistics and leaderboard.'
                            : 'These records are tracked separately and do not count in sales stats.'
                          }
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Export Tab */}
        {isAdmin() && (
          <TabsContent value="export" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Download className="w-6 h-6" />
                    Export Sales Data
                  </h3>
                  <p className="text-violet-100 mt-1">Download records with optional filters</p>
                </div>
                <CardContent className="p-6 space-y-6">
                  {/* Format Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Export Format</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          exportFormat === 'csv'
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                            : 'border-border hover:border-violet-300'
                        }`}
                        onClick={() => setExportFormat('csv')}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${exportFormat === 'csv' ? 'bg-violet-500 text-white' : 'bg-muted'}`}>
                            <FileSpreadsheet className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">CSV</p>
                            <p className="text-xs text-muted-foreground">Comma Separated</p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          exportFormat === 'xlsx'
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                            : 'border-border hover:border-violet-300'
                        }`}
                        onClick={() => setExportFormat('xlsx')}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${exportFormat === 'xlsx' ? 'bg-violet-500 text-white' : 'bg-muted'}`}>
                            <FileSpreadsheet className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">Excel</p>
                            <p className="text-xs text-muted-foreground">XLSX Format</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-sm font-medium">Export Filters (Optional)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Date From</Label>
                        <Input
                          type="date"
                          value={exportFilters.date_from}
                          onChange={(e) => setExportFilters({...exportFilters, date_from: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Date To</Label>
                        <Input
                          type="date"
                          value={exportFilters.date_to}
                          onChange={(e) => setExportFilters({...exportFilters, date_to: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Offer Name</Label>
                        <Input
                          placeholder="Filter by offer"
                          value={exportFilters.offer_name}
                          onChange={(e) => setExportFilters({...exportFilters, offer_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Agent</Label>
                        <Input
                          placeholder="Filter by agent"
                          value={exportFilters.agent}
                          onChange={(e) => setExportFilters({...exportFilters, agent: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Export Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleExport(true)}
                      disabled={exporting}
                      className="w-full h-12 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                      data-testid="export-btn"
                    >
                      {exporting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Preparing Export...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Export with Filters
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleExport(false)}
                      disabled={exporting}
                      variant="outline"
                      className="w-full h-12"
                    >
                      <Database className="w-5 h-5 mr-2" />
                      Export All Records (No Limit)
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-base">Export Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Database className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">All Records</p>
                      <p className="text-xs text-muted-foreground">Export all records without any limit</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Filter className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Filter Options</p>
                      <p className="text-xs text-muted-foreground">Date range, Offer, Agent filters</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <Clock className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Audit Trail</p>
                      <p className="text-xs text-muted-foreground">All exports are logged</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Record Details
              {selectedCustomer?.record_type === 'error' ? (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Error Record
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Sale Done
                </span>
              )}
            </DialogTitle>
            <DialogDescription>Complete information for this record</DialogDescription>
          </DialogHeader>
          {selectedCustomer && (
            <div className="grid grid-cols-2 gap-4 py-4">
              {selectedCustomer.record_type === 'error' ? (
                // Error record fields
                [
                  { icon: Calendar, label: 'Date', value: selectedCustomer.date },
                  { icon: Globe, label: 'Website', value: selectedCustomer.website },
                  { icon: Tag, label: 'Offer Name', value: selectedCustomer.offer_name },
                  { icon: User, label: 'Name', value: selectedCustomer.name },
                  { icon: AlertCircle, label: 'Error', value: selectedCustomer.error },
                  { icon: Phone, label: 'Phone', value: selectedCustomer.phone_number },
                  { icon: User, label: 'Agent Name', value: selectedCustomer.agent_name },
                  { icon: Mail, label: 'Email', value: selectedCustomer.email },
                  { icon: CreditCard, label: 'Bank', value: selectedCustomer.bank },
                ].map((field) => (
                  <div key={field.label} className={`flex items-start gap-3 p-3 rounded-lg ${field.label === 'Error' ? 'col-span-2 bg-red-50 dark:bg-red-900/20' : 'bg-muted/50'}`}>
                    <field.icon className={`w-4 h-4 mt-0.5 ${field.label === 'Error' ? 'text-red-500' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">{field.label}</p>
                      <p className={`text-sm font-medium ${field.label === 'Error' ? 'text-red-700 dark:text-red-400' : 'text-foreground'}`}>{field.value || '-'}</p>
                    </div>
                  </div>
                ))
              ) : (
                // Sale done record fields
                [
                  { icon: Calendar, label: 'Date', value: selectedCustomer.date },
                  { icon: Globe, label: 'Website', value: selectedCustomer.website },
                  { icon: Tag, label: 'Offer Name', value: selectedCustomer.offer_name },
                  { icon: CreditCard, label: 'Credit Score', value: selectedCustomer.credit_score },
                  { icon: FileText, label: 'Verification ID', value: selectedCustomer.verification_id },
                  { icon: User, label: 'First Name', value: selectedCustomer.first_name },
                  { icon: User, label: 'Last Name', value: selectedCustomer.last_name },
                  { icon: User, label: 'Full Name', value: selectedCustomer.name },
                  { icon: FileText, label: 'Reason', value: selectedCustomer.reason },
                  { icon: Clock, label: 'Est. Time', value: selectedCustomer.est_time },
                  { icon: MapPin, label: 'Address', value: selectedCustomer.address },
                  { icon: MapPin, label: 'City', value: selectedCustomer.city },
                  { icon: MapPin, label: 'State', value: selectedCustomer.state },
                  { icon: MapPin, label: 'Zipcode', value: selectedCustomer.zipcode },
                  { icon: Mail, label: 'Email', value: selectedCustomer.email },
                  { icon: Phone, label: 'Phone', value: selectedCustomer.phone_number },
                ].map((field) => (
                  <div key={field.label} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <field.icon className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">{field.label}</p>
                      <p className="text-sm font-medium text-foreground">{field.value || '-'}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${hardDelete ? 'text-destructive' : 'text-amber-500'}`} />
              {hardDelete ? 'Permanently Delete?' : 'Move to Trash?'}
            </DialogTitle>
            <DialogDescription>
              {hardDelete
                ? 'This action cannot be undone. The record will be permanently deleted.'
                : 'The record will be moved to trash and can be restored later.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant={hardDelete ? 'destructive' : 'default'} onClick={confirmDelete}>
              {hardDelete ? 'Delete Permanently' : 'Move to Trash'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-5 h-5" />
              Delete All Records?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete ALL {pagination.total.toLocaleString()} records. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAll} disabled={deletingAll}>
              {deletingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Yes, Delete All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
